import { useState, useCallback, useMemo, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuthStore } from "@/stores/authStore";
import { CONSOLE_PERMISSIONS } from "@/constants/permissions";
import { queryKeys } from "@/lib/query-keys";
import {
  fetchTreatmentOverview,
  fetchTreatmentOverviewByPatient,
} from "@/lib/treatment-overview-api";
import type {
  TreatmentOverviewFilters,
  PatientSearchResult,
} from "@/types/treatment-overview";

const formatDateForApi = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getDefaultTreatmentOverviewFilters = (
  useDefaultDateRange: boolean
): TreatmentOverviewFilters => ({
  providerType: null, // null = all providers
  adviceType: null,
  startDate: useDefaultDateRange ? "2024-01-01" : null,
  endDate: useDefaultDateRange ? formatDateForApi(new Date()) : null,
});

/**
 * Levenshtein-based fuzzy name matching (ported from Flutter).
 * Returns true if similarity >= 95%.
 */
const isNameMatch = (
  providerName: string,
  currentUserName: string
): boolean => {
  if (!providerName || !currentUserName) return false;

  const normalizeName = (name: string): string =>
    name
      .toLowerCase()
      .replace(/\s+/g, " ")
      .trim()
      .replace(/^(dr\.?|doctor|prof\.?|professor)\s*/i, "")
      .replace(/[^\w\s]/g, "");

  const s1 = normalizeName(providerName);
  const s2 = normalizeName(currentUserName);

  // Levenshtein distance
  const len1 = s1.length;
  const len2 = s2.length;
  const matrix: number[][] = Array.from({ length: len1 + 1 }, (_, i) =>
    Array.from({ length: len2 + 1 }, (_, j) => (i === 0 ? j : j === 0 ? i : 0))
  );

  for (let i = 1; i <= len1; i++) {
    for (let j = 1; j <= len2; j++) {
      const cost = s1[i - 1] === s2[j - 1] ? 0 : 1;
      matrix[i][j] = Math.min(
        matrix[i - 1][j] + 1,
        matrix[i][j - 1] + 1,
        matrix[i - 1][j - 1] + cost
      );
    }
  }

  const distance = matrix[len1][len2];
  const maxLength = Math.max(len1, len2);
  if (maxLength === 0) return false;

  const similarity = (1 - distance / maxLength) * 100;
  return similarity >= 95.0;
};

interface UseTreatmentOverviewOptions {
  /** Provided in consultation flow */
  appointmentId?: string;
  /** Provided in patient profile flow */
  patientId?: string;
}

interface TreatmentOverviewDefaultDateRange {
  startDate: string | null;
  endDate: string | null;
}

export const useTreatmentOverview = ({
  appointmentId,
  patientId,
}: UseTreatmentOverviewOptions = {}) => {
  const { user, hasPermission } = useAuthStore();
  const canSearchStandalonePatients = hasPermission(
    CONSOLE_PERMISSIONS.VIEW_TREATMENT_OVERVIEW
  );

  // Determine mode
  const isFixedPatientFlow = !!patientId && !appointmentId;
  const isAdminFlow =
    !appointmentId && !patientId && canSearchStandalonePatients;
  const isConsultationFlow = !!appointmentId;
  const shouldUseDefaultDateRange = isAdminFlow || isFixedPatientFlow;
  const defaultFilters = useMemo(
    () => getDefaultTreatmentOverviewFilters(shouldUseDefaultDateRange),
    [shouldUseDefaultDateRange]
  );
  const defaultDateRange = useMemo<TreatmentOverviewDefaultDateRange>(
    () => ({
      startDate: defaultFilters.startDate,
      endDate: defaultFilters.endDate,
    }),
    [defaultFilters.endDate, defaultFilters.startDate]
  );

  // Filter state
  const [filters, setFilters] = useState<TreatmentOverviewFilters>(() =>
    getDefaultTreatmentOverviewFilters(shouldUseDefaultDateRange)
  );

  // Patient selection state (admin flow)
  const [selectedPatient, setSelectedPatient] =
    useState<PatientSearchResult | null>(null);

  // Build filter params for query key
  const filterParams = useMemo(
    () => ({
      providerType: filters.providerType?.length
        ? filters.providerType
        : undefined,
      adviceType: filters.adviceType?.length ? filters.adviceType : undefined,
      startDate: filters.startDate || undefined,
      endDate: filters.endDate || undefined,
    }),
    [filters]
  );

  // Query ID = appointmentId (consultation) or patientId (admin)
  const queryId = isConsultationFlow
    ? appointmentId
    : patientId || selectedPatient?.id || "";

  // Treatment data query
  const {
    data: treatmentData = [],
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: queryKeys.consultation.patientData.treatmentOverview(
      queryId,
      filterParams
    ),
    queryFn: () => {
      if (isConsultationFlow && appointmentId) {
        return fetchTreatmentOverview(appointmentId, filters);
      }
      if (patientId) {
        return fetchTreatmentOverviewByPatient(patientId, filters);
      }
      if (selectedPatient?.id) {
        return fetchTreatmentOverviewByPatient(selectedPatient.id, filters);
      }
      return Promise.resolve([]);
    },
    enabled: isConsultationFlow
      ? !!appointmentId
      : !!(patientId || selectedPatient?.id),
  });

  // Filter update handlers
  const updateProviderType = useCallback((value: string) => {
    if (value === "all") {
      setFilters((prev) => ({ ...prev, providerType: null }));
      return;
    }
    setFilters((prev) => {
      const current = prev.providerType ?? [];
      const next = current.includes(value)
        ? current.filter((v) => v !== value)
        : [...current, value];
      return { ...prev, providerType: next.length > 0 ? next : null };
    });
  }, []);

  const updateAdviceType = useCallback((value: string) => {
    if (value === "all") {
      setFilters((prev) => ({ ...prev, adviceType: null }));
      return;
    }
    setFilters((prev) => {
      const current = prev.adviceType ?? [];
      const next = current.includes(value)
        ? current.filter((v) => v !== value)
        : [...current, value];
      return { ...prev, adviceType: next.length > 0 ? next : null };
    });
  }, []);

  const updateDateRange = useCallback(
    (startDate: string | null, endDate: string | null) => {
      setFilters((prev) => ({
        ...prev,
        startDate,
        endDate,
      }));
    },
    []
  );

  const clearFilters = useCallback(() => {
    setFilters(defaultFilters);
  }, [defaultFilters]);

  // Patient selection handlers (admin flow)
  const selectPatient = useCallback((patient: PatientSearchResult) => {
    setSelectedPatient(patient);
  }, []);

  const clearPatientSelection = useCallback(() => {
    setSelectedPatient(null);
  }, []);

  // Check if the current user can view notes for a given provider
  const canViewNotes = useCallback(
    (providerName: string): boolean => {
      const currentUserName = user
        ? `${user.first_name} ${user.last_name}`.trim()
        : "";
      return isNameMatch(providerName, currentUserName);
    },
    [user]
  );

  const isUsingDefaultDateRange =
    shouldUseDefaultDateRange &&
    filters.startDate === defaultFilters.startDate &&
    filters.endDate === defaultFilters.endDate;

  // Track provider types seen across data fetches so filter options persist
  // once the user narrows the list. Resets when the target patient changes.
  const [seenProviderTypes, setSeenProviderTypes] = useState<string[]>([]);

  useEffect(() => {
    setSeenProviderTypes([]);
  }, [queryId]);

  useEffect(() => {
    if (!treatmentData.length) return;
    setSeenProviderTypes((prev) => {
      const existing = new Set(prev);
      const next = [...prev];
      for (const item of treatmentData) {
        if (item.provider_type && !existing.has(item.provider_type)) {
          existing.add(item.provider_type);
          next.push(item.provider_type);
        }
      }
      return next.length === prev.length ? prev : next;
    });
  }, [treatmentData]);

  const providerTypeOptions = useMemo(
    () =>
      [...seenProviderTypes]
        .sort((a, b) => a.localeCompare(b))
        .map((label) => ({ label, value: label.toLowerCase() })),
    [seenProviderTypes]
  );

  return {
    // Mode
    isAdminFlow,
    isConsultationFlow,
    isFixedPatientFlow,

    // Treatment data
    treatmentData,
    isLoading,
    isError,
    error,
    refetch,

    // Filters
    filters,
    providerTypeOptions,
    updateProviderType,
    updateAdviceType,
    updateDateRange,
    clearFilters,
    isUsingDefaultDateRange,
    defaultDateRange,

    // Patient search (admin flow)
    selectedPatient,
    selectPatient,
    clearPatientSelection,

    // Note visibility
    canViewNotes,
  };
};
